/**
 * @file FontRoleChips.tsx
 * @description Режим «По роли сайта» поля «Шрифт» элемента (phase19.16, партия
 * SB120 «свобода выбора», этап 0; site-builder.md SB120, SB10; uiux-sb120-
 * fields §4): три роли «Аа Заголовки / Аа Основной / Аа Акцент» (образец «Аа»
 * шрифтом роли) и строка «Montserrat · общий для сайта · Сменить →» —
 * «Сменить» ведёт в «Оформление → Шрифты» (`onOpenSiteFonts`), потому что
 * шрифт роли общий для всего сайта.
 *
 * `auto` («как у сайта») — ни одна роль не выбрана; роль, которой элемент
 * пишется по умолчанию (`autoRole`), обведена пунктиром. Акцент без шрифта —
 * образец и строка шрифтом заголовков с пояснением (как `effectiveFontKey`).
 */

import { effectiveFontKey, FONT_ROLES, isShopFontKey, type FontRole, type SiteFontRegistry, type SiteTheme, type TextFontRole } from "@chaiberi/shared";
import type { KeyboardEvent } from "react";

import { FontGlyph } from "../../site-builder/section/elements/FontGlyph";
import { FONT_CHIP_ROLES } from "../../../lib/site-fields/font-field-model";
import { JUMP_LINK_CLASS } from "../../ui/jump-link";
import { cn } from "@/lib/utils";

interface Props {
  role: TextFontRole;
  /** Роль, которой элемент пишется «как у сайта»; нет — без пунктира. */
  autoRole?: FontRole;
  theme: SiteTheme;
  registry: SiteFontRegistry;
  disabled?: boolean;
  onPick: (role: FontRole) => void;
  /** «Сменить →» — во «Оформление → Шрифты» с раскрытой ролью. */
  onOpenSiteFonts?: (role: FontRole) => void;
}

/** Название шрифта по ключу; свой, которого нет в реестре, — «свой шрифт». */
function fontName(key: string, registry: SiteFontRegistry): string {
  return registry.find(key)?.family ?? (isShopFontKey(key) ? "свой шрифт" : key);
}

export function FontRoleChips({ role, autoRole, theme, registry, disabled, onPick, onOpenSiteFonts }: Props) {
  const shown: FontRole | undefined = role === "auto" ? autoRole : role;
  const effective = shown ? effectiveFontKey(shown, theme) : null;

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const step = event.key === "ArrowRight" || event.key === "ArrowDown" ? 1 : event.key === "ArrowLeft" || event.key === "ArrowUp" ? -1 : 0;
    if (step === 0 || disabled) return;
    event.preventDefault();
    const index = FONT_CHIP_ROLES.findIndex((chip) => chip.role === (shown ?? "heading"));
    const next = FONT_CHIP_ROLES[(index + step + FONT_CHIP_ROLES.length) % FONT_CHIP_ROLES.length];
    if (!next) return;
    onPick(next.role);
    // Фокус — за выбором: у прежней кнопки tabIndex станет -1 (как у радиокнопок).
    event.currentTarget.querySelector<HTMLButtonElement>(`[data-font-role="${next.role}"]`)?.focus();
  }

  return (
    <div className="flex min-w-0 flex-col gap-1">
      <div role="radiogroup" aria-label="Шрифт роли сайта" onKeyDown={onKeyDown} className="grid grid-cols-3 gap-1">
        {FONT_CHIP_ROLES.map((chip) => {
          const active = chip.role === role;
          const inherited = role === "auto" && chip.role === autoRole;
          const key = effectiveFontKey(chip.role, theme).key;
          return (
            <button
              key={chip.role}
              data-font-role={chip.role}
              type="button"
              role="radio"
              aria-checked={active}
              aria-label={inherited ? `${chip.label} — как у сайта` : chip.label}
              tabIndex={active || (role === "auto" && (inherited || (!autoRole && chip.role === "heading"))) ? 0 : -1}
              disabled={disabled}
              onClick={() => onPick(chip.role)}
              className={cn(
                "flex min-h-16 min-w-0 flex-col items-center justify-center gap-1 rounded-lg px-1 py-2 outline-none transition-colors",
                "focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50",
                active
                  ? "bg-accent text-on-surface [box-shadow:inset_0_0_0_2px_var(--primary)]"
                  : "bg-surface-low text-on-surface-variant hover:text-on-surface",
                inherited && "outline-dashed outline-1 outline-[var(--outline-variant)]",
              )}
            >
              <FontGlyph fontKey={key} weight={400} />
              <span className="type-caption max-w-full truncate">{chip.label}</span>
            </button>
          );
        })}
      </div>
      {shown && effective ? (
        <p className="type-caption flex flex-wrap items-center gap-x-1 text-on-surface-variant">
          <span className="min-w-0 break-words">
            {effective.fallback
              ? `Акцент не выбран — сейчас шрифт ${FONT_ROLES.heading.genitive} · ${fontName(effective.key, registry)}`
              : `${fontName(effective.key, registry)} · общий для сайта`}
            {role === "auto" ? " · как у сайта" : null}
          </span>
          {onOpenSiteFonts ? (
            <>
              <span aria-hidden>·</span>
              <button
                type="button"
                className={cn(JUMP_LINK_CLASS, "text-sm")}
                aria-label="Сменить шрифт роли в оформлении сайта"
                onClick={() => onOpenSiteFonts(shown)}
              >
                Сменить →
              </button>
            </>
          ) : null}
        </p>
      ) : null}
    </div>
  );
}
